import { Helmet } from "react-helmet-async";
import type { ReactNode } from "react";
import { useLocation } from "react-router";

interface PageLayoutProps {
  title: string;
  description: string;
  ogImage?: string;
  ogType?: string;
  children: ReactNode;
}

const SITE_NAME = "Huntbase";
// huntbase.io 301s to www — canonicals and preview URLs must use the final host.
const SITE_URL = import.meta.env.VITE_SITE_URL || "https://www.huntbase.io";
const DEFAULT_OG_IMAGE = "/assets/og-image.png";

export function PageLayout({
  title,
  description,
  ogImage = DEFAULT_OG_IMAGE,
  ogType = "website",
  children,
}: PageLayoutProps) {
  const { pathname } = useLocation();
  // Same rule as formatTitle() in functions/_shared/site-meta.js: no suffix when
  // the title already names the brand.
  const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
  const canonicalUrl = `${SITE_URL}${pathname}`;
  const fullOgImageUrl = ogImage.startsWith("http") ? ogImage : `${SITE_URL}${ogImage}`;

  return (
    <>
      <Helmet>
        <title>{fullTitle}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={fullTitle} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={fullOgImageUrl} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content={ogType} />
        <meta property="og:site_name" content={SITE_NAME} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={fullTitle} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={fullOgImageUrl} />
      </Helmet>
      {children}
    </>
  );
}
